
import React, { useState } from 'react';
import { Technique } from '@/types/technique';
import type { TechniqueOffline } from '@/services/offline/indexeddb/schema';
import { usePermissions } from '@/contexts/unified-permissions';
import { TechniqueHeader } from './TechniqueHeader';
import { TechniqueMedia } from './TechniqueMedia';
import { TechniqueInfoCards } from './TechniqueInfoCards';
import { TechniqueNotesCard } from './TechniqueNotesCard';
import { YouTubeVideosList } from './YouTubeVideosList';
import { TechniqueLoadingState } from './TechniqueLoadingState';
import { useOfflineTechniqueNotes } from '@/hooks/useOfflineTechniqueNotes';
import { useTechniqueVideos } from '@/hooks/useTechniqueVideos';
import { ShareTechniqueDialog } from './sharing/ShareTechniqueDialog';

/**
 * Transform TechniqueOffline to Technique for UI components
 */
function transformTechniqueOfflineToTechnique(offline: TechniqueOffline): Technique {
  return {
    ...offline,
    is_user_created: Boolean(offline.is_user_created), // Convert from number to boolean
    is_private: Boolean(offline.is_private), // Convert from number to boolean
  };
}

/**
 * Check if technique is TechniqueOffline type
 */
function isTechniqueOffline(technique: Technique | TechniqueOffline): technique is TechniqueOffline {
  return typeof (technique as TechniqueOffline).is_user_created === 'number';
}

interface TechniqueDetailsProps {
  technique: Technique | TechniqueOffline;
  isSaved: boolean;
  onToggleSave: (e: React.MouseEvent) => void;
  isLoading?: boolean;
}

export const TechniqueDetails = ({
  technique,
  isSaved,
  onToggleSave,
  isLoading = false
}: TechniqueDetailsProps) => {
  const { isAuthenticated, user } = usePermissions();
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);

  // Convert TechniqueOffline to Technique if needed
  const normalizedTechnique: Technique = isTechniqueOffline(technique)
    ? transformTechniqueOfflineToTechnique(technique)
    : technique;
  
  const {
    notes,
    isSavingNotes,
    initialNotes,
    handleNotesChange,
    saveNotes
  } = useOfflineTechniqueNotes(normalizedTechnique.id, user?.id, isAuthenticated);

  // Use the updated hook with a string ID rather than an array
  const {
    videos,
    handleVideoAdded,
    sortBy,
    handleSortChange
  } = useTechniqueVideos(normalizedTechnique.id);

  // Provide default values for required props
  const safeHandleVideoAdded = handleVideoAdded || (async () => {});
  const safeSortBy = sortBy || 'newest';
  const safeHandleSortChange = handleSortChange || (() => {});

  if (isLoading) {
    return <TechniqueLoadingState />;
  }

  return (
    <div className="space-y-8">
      {/* Technique Header Section */}
      <TechniqueHeader
        technique={normalizedTechnique}
        isSaved={isSaved}
        onToggleSave={onToggleSave}
        onShare={() => setIsShareDialogOpen(true)}
      />

      {/* Technique Media Section */}
      <TechniqueMedia technique={normalizedTechnique} />

      {/* Technique Description */}
      <div className="prose dark:prose-invert max-w-none">
        <p>{normalizedTechnique.description}</p>
      </div>

      {/* YouTube Videos Section */}
      {isAuthenticated && (
        <YouTubeVideosList
          videosList={videos || []}
          techniqueId={normalizedTechnique.id}
          onVideoAdded={safeHandleVideoAdded}
          onVideoDeleted={safeHandleVideoAdded}
          sortBy={safeSortBy}
          onSortChange={safeHandleSortChange}
          className="pt-2"
        />
      )}

      {/* Technique Info Cards Section */}
      <TechniqueInfoCards technique={normalizedTechnique} />

      {/* Personal Notes Section */}
      <TechniqueNotesCard
        notes={notes}
        isAuthenticated={isAuthenticated}
        isSavingNotes={isSavingNotes}
        initialNotes={initialNotes}
        onNotesChange={handleNotesChange}
        onSaveNotes={saveNotes}
      />

      {/* Share Technique Dialog */}
      <ShareTechniqueDialog
        open={isShareDialogOpen}
        onOpenChange={setIsShareDialogOpen}
        technique={normalizedTechnique}
      />
    </div>
  );
};
